import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@src/lib/utils/cn";
import { MENU_MOTION_CLASSNAME } from "@src/lib/ui/menuMotion";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverClose = PopoverPrimitive.Close;

// Fade + slight zoom + a 2px slide away from the trigger. The default, so
// editors that open in place over a field don't appear to make it jump.
const POPOVER_DEFAULT_MOTION_CLASSNAME =
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";

// The menu open/close motion (src/lib/ui/menuMotion.ts), growing from the
// side and alignment the popover opens from.
const POPOVER_MENU_MOTION_CLASSNAME = cn(
  "origin-[var(--radix-popover-content-transform-origin)]",
  MENU_MOTION_CLASSNAME,
);

export type PopoverMotion = "default" | "menu";

type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> & {
  disablePortal?: boolean;
  /**
   * "menu" opts into the DropdownMenu/Select open/close motion; use it for
   * popovers that read as menus. "default" keeps the fade + zoom.
   */
  motion?: PopoverMotion;
};

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      align = "center",
      sideOffset = 4,
      disablePortal,
      motion = "default",
      ...props
    },
    ref,
  ) => {
    const content = (
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "z-50 min-w-[8rem] overflow-hidden rounded-xl border border-border-primary bg-bg-elevated p-4 text-text-primary shadow-large outline-none popover-content-available-height",
          motion === "menu"
            ? POPOVER_MENU_MOTION_CLASSNAME
            : POPOVER_DEFAULT_MOTION_CLASSNAME,
          className,
        )}
        {...props}
      />
    );

    if (disablePortal) return content;

    return <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>;
  },
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverClose };
